import _ from 'lodash'
import { css } from 'styled-components'
import { BoxProps } from '../components/primitives/Box'
import {
  BreakpointProp,
  Breakpoints,
  ResponsiveSpaceScaleValue,
} from './stylePropTypes'

const retinaDisplay = `only screen and (-webkit-min-device-pixel-ratio: 1.25),
only screen and (min--moz-device-pixel-ratio: 1.25),
only screen and (-o-min-device-pixel-ratio: 1.25/1),
only screen and (min-device-pixel-ratio: 1.25),
only screen and (min-resolution: 200dpi),
only screen and (min-resolution: 1.25dppx)`

/**
 * @param srcSet E.g. "/static/676fb0d829dfe1ca6fed6b7a04f035f6/bbf24/insights-hero.jpg 750w,\n/static/676fb0d829dfe1ca6fed6b7a04f035f6/5cfca/insights-hero.jpg 1080w,\n/static/676fb0d829dfe1ca6fed6b7a04f035f6/bb0a0/insights-hero.jpg 1366w,\n/static/676fb0d829dfe1ca6fed6b7a04f035f6/16812/insights-hero.jpg 1920w"
 * @returns
 */
export function responsiveBackgroundImage(
  srcSet: string,
  backgroundColor: string,
) {
  const sourcesWithWidths = srcSet.split(',\n').map(src => {
    const match = src.match(/(.*) (\d+)w/)
    return {
      url: match?.[1],
      width: Number(match?.[2]),
    }
  })

  return css`
    background-color: ${backgroundColor};
    background-image: url(${sourcesWithWidths[0].url});

    ${sourcesWithWidths.map(
      source => css`
        @media screen and (min-width: ${source.width}px) {
          background-image: url(${source.url});
        }
      `,
    )}

    ${sourcesWithWidths.map(
      source => css`
        @media ${retinaDisplay} and (min-width: ${source.width / 2}px) {
          background-image: url(${source.url});
        }
      `,
    )}
  `
}

export function svgToImage(src: string, backgroundColor: string) {
  return {
    images: {
      fallback: { src: '', srcSet: `${src} 1000w` },
    },
    backgroundColor,
  }
}

/**
 * Allows using responsive props syntax with a custom property.
 * The idea is to transform a custom responsive property to
 * BoxProps that styled-system can then create responsive styles for.
 *
 * E.g.
 * "maybeResponsiveValue" value: {xs: 'small', md: 'medium'}
 * "propValues" value:
 * {
 *   small: {
 *     fontSize: 14,
 *     borderRadius: 4,
 *     paddingX: 10,
 *     height: 32
 *   },
 *   medium: {
 *     fontSize: 16,
 *     paddingX: 16,
 *     borderRadius: 4,
 *     height: 40
 *   },
 *   large: {
 *     fontSize: 20,
 *     paddingX: 32,
 *     borderRadius: 8,
 *     height: 56
 *   }
 * }
 *
 * Results in:
 * {
 *  "fontSize": {
 *    "xs": 14,
 *    "md": 16
 *  },
 *  "borderRadius": {
 *    "xs": 4,
 *    "md": 4
 *  },
 *  "paddingX": {
 *    "xs": 10,
 *    "md": 16
 *  },
 *  "height": {
 *    "xs": 32,
 *    "md": 40
 *  }
 *}
 */
export function transformResponsiveProp<
  T extends string,
  V extends T | BreakpointProp<T>,
>(maybeResponsiveValue: V, propValues: Record<T, Partial<BoxProps>>) {
  if (typeof maybeResponsiveValue === 'string') {
    return propValues[maybeResponsiveValue as string]
  }

  const resultingProps = {}
  const allProps = new Set(
    _.flatten(
      Object.values<Partial<BoxProps>>(propValues).map(obj => Object.keys(obj)),
    ),
  )
  Array.from(allProps).forEach(prop => {
    resultingProps[prop] = Object.entries(maybeResponsiveValue).reduce(
      (responsiveProp, [breakpoint, value]) => {
        responsiveProp[breakpoint] = propValues[value][prop]
        return responsiveProp
      },
      {} as Record<Breakpoints, Partial<BoxProps>>,
    )
  })
  return resultingProps
}

export function responsiveScale(
  value: 32 | 48 | 64 | 92,
): ResponsiveSpaceScaleValue {
  switch (value) {
    case 32:
      return { xs: 16, sm: value }
    case 48:
      return { xs: 24, sm: value }
    case 64:
      return { xs: 40, sm: value }
    case 92:
      return { xs: 64, sm: value }
    default:
      return value
  }
}
